import { financialTransactions } from './../../../../financial-transactions/state/financial-transactions.selectors';
import { GAService } from './../../../../../shared/services/ga.service';
import { MixpanelService } from './../../../../../shared/services/mixpanel.service';
import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ValidationErrors,
} from '@angular/forms';
import { OverlayService } from 'src/app/shared/modules/overlay/services/overlay.service';
import { MoneyValidator } from 'src/app/shared/validators/money.validator';
import { FinancialTransactionActions } from 'src/app/features/financial-transactions/state/financial-transactions.action-types';
import {
  savingSuccessStateById,
  balanceAvailable,
} from 'src/app/features/financial-transactions/state/financial-transactions.selectors';
import { ConfirmInvestmentComponent } from '../../organisms/confirm-investment/confirm-investment.component';
import { InvoiceService } from '../../../../invoices/services/invoice.service';
import { AppState } from 'src/app/app.states';
import { Store } from '@ngrx/store';
import { take, takeUntil } from 'rxjs/operators';
import { InvoiceSelectors } from 'src/app/features/invoices/state/invoices.selector-types';
import { Subject } from 'rxjs';
import { ObservableInvestmentValidator } from 'src/app/shared/validators/observe-investment.validator';
import { GTMService } from 'src/app/shared/services/gtm.service';
import { FormService } from 'src/app/shared/services/form.service';
import { UsersSelectors } from 'src/app/features/users/state/users.selector-types';
import { UsersActions } from 'src/app/features/users/state/users.action-types';
import { WindowScrollService } from 'src/app/shared/services/window-scroll.service';
import moment from 'moment';
import { CapitalizeOnlyFirstLetter } from '../../../../../shared/pipes/capitalize-only-first-letter.pipe';
import { NumberSpacingPipe } from '../../../../../shared/pipes/number-spacing.pipe';
import { HistoricalInvestmentDialogComponent } from '../../organisms/historical-investment-dialog/historical-investment-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import {
  BreakpointObserver,
  Breakpoints,
  BreakpointState,
} from '@angular/cdk/layout';
import { environment } from 'src/environments/environment';
import {
  riskCriterion,
  riskForeignTrade,
  riskForeignTradeYear,
  riskSoles,
} from 'src/app/shared/util/risk-data.util';
import { UserStatus } from 'src/app/shared/enums/UserStatus.enum';
import { FeatureFlagService } from 'src/app/shared/services/feature-flag.service';
import { InvestorCapabilitiesService } from 'src/app/shared/services/investor-capabilities.service';

@Component({
  selector: 'app-invest-in-invoice',
  templateUrl: './invest-in-invoice.component.html',
  styleUrls: ['./invest-in-invoice.component.scss'],
})
export class InvestInInvoiceComponent implements OnInit {
  @Input() payload;
  form: FormGroup;
  @Output() destroySelf = new EventEmitter();
  calculatedROI;
  availableBalance;
  debtorStats;
  InvoiceAvailableBalance;
  ngUnsubscribe: Subject<void> = new Subject<void>();
  userStatus = '';
  investorProfile;
  disclaimerAlert = false;
  isDisclaimerVisible = false;
  subscription;
  userRole = '';
  parentTab;
  codes = '';

  /** The year of the imports and exports shown: the newest one the evaluation carries. */
  get operationYear(): string {
    return riskForeignTradeYear(this.payload?.evaluation);
  }

  tabInvoiceDetail = true;
  tabRiskData = false;
  tabOperations = false;
  tabHistory = false;
  selectedTab;
  ratingDisclaimerOpen = false;
  isMobile;
  constructor(
    private fb: FormBuilder,
    private overlayService: OverlayService,
    private invoiceService: InvoiceService,
    private store: Store<AppState>,
    private investmentValidator: ObservableInvestmentValidator,
    private gtmService: GTMService,
    private formService: FormService,
    private mixpanelService: MixpanelService,
    private gaService: GAService,
    private windowScrollService: WindowScrollService,
    private capitalizeOnlyFirstLetter: CapitalizeOnlyFirstLetter,
    private numberSpacing: NumberSpacingPipe,
    public dialog: MatDialog,
    private breakpointObserver: BreakpointObserver,
    private featureFlagService: FeatureFlagService,
    private investorCapabilities: InvestorCapabilitiesService
  ) {}

  /**
   * FIN-1709 · AC4 — pre-approval opens the deposit funnel and must not open investing.
   *
   * ⚠ Nothing was stopping an `email verified` inversionista from investing: the two
   * conditions that mention the status guard a pair of headings, and `isDisclaimerVisible`
   * and `getClass()` are read by nobody. It went unnoticed because such an account could
   * never fund an investment — which is precisely what this ticket changes.
   *
   * The button is greyed rather than removed: the inversionista is meant to see that
   * investing is where this leads once they are approved, not to find the screen missing
   * a control it has for everyone else.
   *
   * Flag off the answer is an unconditional yes, so the screen renders exactly as it does
   * today for everyone.
   */
  canInvest(): boolean {
    if (!this.featureFlagService.isEarlyDepositEnabled()) {
      return true;
    }

    return this.investorCapabilities.capabilitiesFor(this.investorProfile).invest;
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  ngOnInit(): void {
    this.parentTab = this.payload.parentTab;
    this.store
      .select(InvoiceSelectors.opportunitiesById(this.payload._id))
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((opportunity) => {
        this.payload = opportunity;

        this.InvoiceAvailableBalance = opportunity.availableBalanceAmount;
      });
    this.invoiceService
      .getDebtorStatsByRUC(this.payload.debtor._id)
      .subscribe((debtorStats) => {
        this.debtorStats = debtorStats;
      });

    this.store
      .select(balanceAvailable(this.payload.currency))
      .pipe(take(1))
      .subscribe((balance) => {
        this.availableBalance = balance;

        this.form = this.fb.group({
          amount: [
            '',
            [Validators.required, MoneyValidator(1, 2)],
            [
              this.investmentValidator.validate(
                this.payload._id,
                balance,
                this.payload.currency
              ),
            ],
          ],
        });

        this.form.get('amount').valueChanges.subscribe((newValue) => {
          if (newValue && Math.sign(Number(newValue)) === 1) {
            this.calculatedROI = this.invoiceService.calculateROI(
              newValue,
              this.payload.tem,
              this.payload.minimumDuration
            );
          } else {
            this.calculatedROI = '00.00';
          }
        });

        this.breakpointObserver
          .observe([Breakpoints.XSmall])
          .subscribe((state: BreakpointState) => {
            if (state.matches) {
              this.isMobile;
              this.defaultLauncher(true);
            }
          });
      });

    this.store
      .select(UsersSelectors.profile())
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((user) => {
        this.investorProfile = user;
        this.userStatus = user.status;
        this.userRole = user.role;
        this.isDisclaimerVisible =
          (user.status === 'email verified' || (this.featureFlagService.isLAFTEnabled() && user.status == UserStatus.Pending)) ? true : false;
      });

    this.subscription = this.overlayService
      .getCloseEmitter()
      .subscribe((obs) => {
        if (obs && obs === 'ConfirmInvestmentComponent') {
          this.sendEventPageView('Detail');
        }
      });
    this.codes = this.payload.physicalInvoices.map((i) => i.code).join('\n');
  }

  submit(event) {
    event.preventDefault();

    // FIN-1709 · AC4. The button is greyed and `pointer-events: none` already stops the
    // click; this is the gate that holds if anything else ever reaches the handler.
    if (!this.canInvest()) {
      return;
    }

    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    if (this.form.valid && !this.getDisable()) {
      this.invest();
      //this.destroySelf.emit();
    } else {
      this.activateDisclaimerAlert();
      let event = {
        event: '[Platform][Form][Input][Error]',
        _inputError: this.formService.getFormValidationErrors(this.form),
        _inputName: 'Opportunities - detail - invertir - error',
        _inputCategory: 'Opportunities - detail - invertir',
      };
      this.gtmService.newEvent(event);
    }
  }

  invest() {
    this.sendEventPageView('Invest Step 1');
    this.overlayService.startConfirmation(
      {
        type: 'investment',
        amount: this.form.value.amount,
        currency: this.payload.currency,
        invoice: this.payload._id,
      },
      ConfirmInvestmentComponent,
      'details',
      'create',
      null,
      FinancialTransactionActions.Investing,
      `/` /* a hack because we create two modals and it will destory the last one only, didn't dig deeper into why it doesn't go back to opportunities list */,
      savingSuccessStateById,
      {
        onSuccess: {
          showCheck: true,
          title: 'Inversión en proceso',
          messsage:
            'La inversión se hará efectiva cuando la factura se financie completamente.',
        },
        onConfirm: {
          title: 'Confirmación de inversión',
        },
      },
      this.investCallback.bind(this)
    );

    let event = {
      event: '[Platform][Form][Input]',
      _inputError: this.formService.getFormValidationErrors(this.form),
      _inputName: 'Opportunities - detail - invertir',
      _inputCategory: 'Opportunities - detail - invertir',
    };
    this.gtmService.newEvent(event);
  }

  async investCallback(success) {
    if (success) {
      this.destroySelf.emit();

      const role = await this.store
        .select(UsersSelectors.profileProperty('role'))
        .pipe(take(1))
        .toPromise();

      if (this.isMobile) this.defaultLauncher(false);

      if (role === 'investor') {
        this.store
          .select(financialTransactions)
          .pipe(take(1))
          .subscribe((trx) => {
            const eventName = 'new_investment';
            const eventData = {
              create_date: new Date().toISOString(),
              operation_type: trx[0].invoice?.isConfirming
                ? 'Confirming'
                : 'Factoring',
              currency: trx[0].currency.toUpperCase(),
              amount: trx[0].amount,
              is_success: true,
              error_description: null,
            };
            this.gaService.sendEvent(eventName, eventData);
            this.mixpanelService.newEvent(eventName, eventData);
            this.sendEventSetupbar(trx);
            this.sendEventPageView('Invest Step 2');
          });
      }
    }
  }

  sendEventSetupbar(trx) {
    this.store
      .select(UsersSelectors.profile())
      .pipe(take(1))
      .subscribe((profile) => {
        let investment = trx.filter(
          (transaction) => transaction.type === 'investment'
        );
        if (profile) {
          if (!profile.setupbarClosed && investment.length == 1) {
            let event = {
              event: '[Platform][Setup][Bar]',
              _inputCategory: 'General / Setup Bar',
              _inputName: 'Invested In An Oportunity In Setup Bar',
            };
            this.gtmService.newEvent(event);
            this.store.dispatch(UsersActions.LoadProfile());
          }
        }
      });
  }

  sendEvent(field) {
    let event;
    if (this.form.get(field).errors) {
      event = {
        event: '[Platform][Form][Input][Error]',
        _inputError: this.form.get(field).errors,
        _inputName: 'Opportunities - detail - monto - error',
        _inputCategory: 'Inversionista - 0pportunities - detail',
      };
    } else {
      event = {
        event: '[Platform][Form][Input]',
        _inputName: 'Opportunities - detail - monto',
        _inputCategory: 'Inversionista - 0pportunities - detail',
      };
    }
    this.gtmService.newEvent(event);
  }

  activateDisclaimerAlert() {
    console.log('entra');
    this.disclaimerAlert = true;
    setTimeout(() => {
      this.disclaimerAlert = false;
    }, 1000);
  }

  ngAfterViewInit() {
    this.sendEventPageView('Detail');
  }

  sendEventPageView(title) {
    this.windowScrollService.sendNewEvent(
      'Opportunities',
      `${title}`,
      this.getTabName()
    );
  }

  getContent() {
    let hour = this.payload?.onSaleSlot === '1' ? '12:30' : '17:30';
    let date =
      moment(this.payload?.onSaleDate).format('dddd DD [de] MMMM, ') + hour;
    return (
      'Fecha de publicación de la factura: ' +
      this.capitalizeOnlyFirstLetter.transform(date)
    );
  }

  getClass() {
    const laftConditional = this.userStatus === 'email verified' || (this.featureFlagService.isLAFTEnabled() && this.userStatus == UserStatus.Pending);

    if (
      laftConditional ||
      (
       ( this.userStatus !== 'email verified' && this.userStatus !== 'pending') &&
        this.payload.hasOwnProperty('onSale') &&
        this.payload?.onSale === false
      )
    ) {
      return 'grayed-out';
    } else {
      return '';
    }
  }

  getDisable() {
    if (
      this.payload.hasOwnProperty('onSale') &&
      this.payload?.onSale === false
    ) {
      return true;
    } else {
      return false;
    }
  }

  updateTabSelected(e) {
    this.selectedTab = e;
    this.sendEventPageView('Detail');
  }

  getRiskData(opportunity, criteria, attribute = 'data') {
    if (
      criteria === 'directDebt' ||
      criteria === 'indirectDebt' ||
      criteria === 'mortgageGuarantees' ||
      criteria === 'unclearProtests'
    ) {
      return riskSoles(opportunity?.evaluation, criteria, this.numberSpacing);
    }

    return riskCriterion(opportunity?.evaluation, criteria, attribute);
  }

  getAdditionalInfo(opportunity, attribute = 'data') {
    return riskForeignTrade(opportunity?.evaluation, attribute, this.numberSpacing);
  }

  openHistoric($event) {
    $event.stopPropagation();
    console.log('Open');
    let dialogRef = this.openDialogHelper(HistoricalInvestmentDialogComponent, {
      data: {
        ...this.payload?.debtor,
        pageSection: 'Mi historial Detail',
      },
    });
  }

  openDialogHelper(dialogComp, data?) {
    const dialogRef = this.dialog.open(dialogComp, {
      maxWidth: '100vw',
      maxHeight: '100vh',
      height: '100%',
      position: {
        left: '0',
      },
      data: data,
    });

    return dialogRef;
  }

  connectDialogListeners(dialogRef, callback) {
    dialogRef.afterClosed().subscribe(callback);
  }

  getTabName() {
    switch (this.selectedTab) {
      case 0:
        return `${this.parentTab} Detalle de la factura`;
      case 1:
        if (this.payload.evaluation) {
          return `${this.parentTab} Data de riesgos`;
        } else {
          return `${this.parentTab} Historial con Finsmart`;
        }
      case 2:
        return `${this.parentTab} Historial con Finsmart`;
      default:
        return `${this.parentTab} Detalle de la factura`;
    }
  }

  defaultLauncher(state) {
    this.windowScrollService.setDefaultLauncher(state);
  }
}
